import React, { useState } from "react";
import { Bell, Plus, LogOut, User } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import NotificationCenter from "@/components/NotificationCenter";

export default function Header() {
  const { setDirectiveOpen, companyParam, companyId, activeCompany } = useApp();
  const { user, logout } = useAuth();
  const [notifOpen, setNotifOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const { data: summary = { unread: 0, critical: 0, needs_action: 0 } } = useQuery({
    queryKey: ["notif-summary", companyId],
    queryFn: async () => (await api.get("/notifications/summary", { params: companyParam })).data,
    refetchInterval: 15000,
  });

  return (
    <header className="h-16 shrink-0 bg-white border-b border-border sticky top-0 z-30 flex items-center justify-between px-6">
      <div className="flex items-center gap-3">
        <span className="w-2 h-2 bg-st-success live-dot" />
        <div className="text-[11px] font-semibold tracking-[0.14em] uppercase text-ink-muted">
          {activeCompany ? activeCompany.name : "All Companies"} · Live
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          data-testid="new-directive-btn"
          onClick={() => setDirectiveOpen(true)}
          className="flex items-center gap-2 bg-brand text-white px-4 py-2 text-sm font-medium hover:bg-brand-dark transition-colors"
        >
          <Plus className="w-4 h-4" strokeWidth={2.5} />
          New Directive
        </button>

        <div className="relative">
          <button
            data-testid="notification-bell"
            onClick={() => setNotifOpen((v) => !v)}
            className="relative w-10 h-10 flex items-center justify-center border border-border hover:border-ink transition-colors"
          >
            <Bell className="w-4.5 h-4.5 text-ink" strokeWidth={2} />
            {summary.unread > 0 && (
              <span
                data-testid="notification-count"
                className={`absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold font-mono text-white ${
                  summary.critical > 0 ? "bg-st-critical live-dot" : "bg-brand"
                }`}
              >
                {summary.unread}
              </span>
            )}
          </button>
          {notifOpen && <NotificationCenter onClose={() => setNotifOpen(false)} />}
        </div>

        <div className="relative">
          <button
            data-testid="user-menu-btn"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex items-center gap-2 pl-2 pr-3 h-10 border border-border hover:border-ink transition-colors"
          >
            <div className="w-6 h-6 bg-ink text-white flex items-center justify-center text-[11px] font-bold font-mono">
              {user?.name?.[0] || "U"}
            </div>
            <div className="text-left leading-tight hidden sm:block">
              <div className="text-xs font-semibold text-ink">{user?.name}</div>
              <div className="text-[10px] uppercase tracking-wider text-ink-faint">{user?.role}</div>
            </div>
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-12 w-56 bg-white border border-ink shadow-xl z-40 animate-in-up">
              <div className="px-4 py-3 border-b border-border">
                <div className="text-sm font-semibold text-ink">{user?.name}</div>
                <div className="text-xs text-ink-muted">{user?.email}</div>
                {user?.title && <div className="text-[11px] text-ink-faint mt-1">{user.title}</div>}
              </div>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="w-full flex items-center gap-2 px-4 py-3 text-sm text-st-critical hover:bg-surface-muted transition-colors"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
